import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop } from '@stencil/core';
import { DEFAULT_WORKBENCH_THEME, WorkbenchTheme } from '../../shared/themes';

// Components
import { TimerAnimation } from './timer-animation/timer-animation';

export type RoundButtonSize = 's' | 'm' | 'l';
export type RoundButtonElevation = '' | 'xs' | 's' | 'm' | 'l';

/**
 * @slot - mandatory label and wb-icon placed within the element
 */
@Component({
  tag: 'wb-round-button',
  styleUrl: './round-button.scss',
  shadow: true,
})
export class RoundButtonComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** the colour scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  /** size of the round button */
  @Prop({ reflect: true })
  public size!: RoundButtonSize;

  /** elevation size consisting of border and shadow. `'m'` and `'l'` not available for dark theme */
  @Prop({ reflect: true })
  public elevation?: RoundButtonElevation;

  /** duration in milliseconds for timer animation */
  @Prop({ reflect: true })
  public duration?: number;

  /** shows the button as disabled and prevents interactions */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** renders an anchor tag linked to the passed in URL (fragment) */
  @Prop()
  public href: string | undefined;

  /** can be used in combination with `href` property */
  @Prop()
  public download: string | undefined;

  /** can be used in combination with `href` property */
  @Prop()
  public rel: string | undefined;

  /** can be used in combination with `href` property */
  @Prop()
  public target: string | undefined;

  /** emitted when the button receives focus */
  @Event()
  public wbfocus!: EventEmitter<void>;

  /** emitted when the button loses focus */
  @Event()
  public wbblur!: EventEmitter<void>;

  private onFocus = () => this.wbfocus.emit();

  private onBlur = () => this.wbblur.emit();

  public render(): JSX.Element {
    return (
      <Host //
        class="wb-round-button"
        aria-disabled={this.disabled ? 'true' : null}
      >
        {this.href ? (
          <a //
            class="button"
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            href={this.href}
            rel={this.rel}
            target={this.target}
            download={this.download}
          >
            <slot />
            {this.duration && <TimerAnimation duration={this.duration} />}
          </a>
        ) : (
          <button //
            class="button"
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            type="button"
            disabled={this.disabled}
          >
            <slot />
            {this.duration && <TimerAnimation duration={this.duration} />}
          </button>
        )}
      </Host>
    );
  }
}
